import { Fragment, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Badge, ConfirmationButton, ConfirmationModal, TooltipCard } from '@components';
import { useDirection } from '@hooks';
import {
  ClassificationConfidence,
  DocumentClassificationCoverage,
  DocumentClassificationInvalidReason,
  DocumentClassificationStatus,
} from '@libs/constants';
import { type SingularKeys, Trans, useTrans } from '@libs/i18n';
import { DocumentClassificationCandidate, DocumentClassificationMetadataDto } from '@libs/types';
import { canMoveToSuggestedDomain, isWeakClassificationMatch } from '@libs/utils';
import { ArrowBendUpRightIcon, LightbulbIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { NoFlipAlert } from '@utils';

/** How sure the AI is of the match, in the user's words. */
const CONFIDENCE_LABEL = {
  [ClassificationConfidence.HIGH]: 'app.assessment.classification.confidence.high',
  [ClassificationConfidence.MEDIUM]: 'app.assessment.classification.confidence.medium',
  [ClassificationConfidence.LOW]: 'app.assessment.classification.confidence.low',
} as const;

/**
 * Colour of the match row, keyed to confidence.
 *
 * The palette is the signal here — there is no score to show. The AI reports
 * high/medium/low, so deriving a percentage from that label would present
 * precision the classifier never produced.
 */
const CONFIDENCE_TONE = {
  [ClassificationConfidence.HIGH]: {
    dot: 'success.main',
    text: 'success.dark',
    border: 'success.light',
    surface: 'background.paper',
  },
  [ClassificationConfidence.MEDIUM]: {
    dot: 'warning.main',
    text: 'warning.dark',
    border: 'warning.light',
    surface: 'warning.lighter',
  },
  [ClassificationConfidence.LOW]: {
    dot: 'error.main',
    text: 'error.dark',
    border: 'error.light',
    surface: 'error.lighter',
  },
} as const;

/**
 * One selectable document in the picker.
 *
 * The score is shown, and this row used to argue it should not be: while
 * candidates were the AI's high/medium/low opinion, a percentage would have
 * been a number we invented, and — the actual objection — once shown, people
 * compare them. They can now, because the number is measured: absolute cosine
 * similarity against the same document, on one scale, comparable across rows
 * and across domains. A field that reads 11%, 9%, 8% is the honest answer that
 * three fit labels reading "low" could never give.
 */
const CandidateRow = observer(({
  name,
  why,
  score,
  selected,
  current = false,
  claimed = false,
  claimedLabel,
  onSelect,
}: {
  name: string;
  why?: string;
  /** Absolute similarity in [0, 1]; omitted for the current-match row. */
  score?: number;
  selected: boolean;
  current?: boolean;
  claimed?: boolean;
  claimedLabel?: string;
  onSelect: () => void;
}) => (
  <Box
    component="button"
    type="button"
    onClick={claimed ? undefined : onSelect}
    disabled={claimed}
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.25,
      width: '100%',
      p: 1,
      textAlign: 'start',
      font: 'inherit',
      borderRadius: 1.5,
      border: '1px solid',
      borderColor: selected ? 'primary.main' : 'divider',
      bgcolor: selected ? 'primary.lighter' : 'background.paper',
      cursor: claimed ? 'not-allowed' : 'pointer',
      opacity: claimed ? 0.7 : 1,
    }}
  >
    <Box
      sx={{
        flexShrink: 0,
        mt: '2px',
        width: 15,
        height: 15,
        borderRadius: '50%',
        boxSizing: 'border-box',
        border: selected ? '5px solid' : '1.5px solid',
        borderColor: selected ? 'primary.main' : 'action.disabled',
      }}
    />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600}>
        {name}
      </Typography>
      {why && (
        <Typography variant="caption" color="text.secondary" display="block">
          {why}
        </Typography>
      )}
      {claimed && claimedLabel && (
        <Box sx={{ mt: 0.5 }}>
          <Badge variant="warningSoft" label={claimedLabel} />
        </Box>
      )}
    </Box>
    {current ? (
      <Chip
        label={<Trans i18nKey="app.assessment.classification.candidates.current" />}
        size="small"
        variant="outlined"
        color="success"
        sx={{ flexShrink: 0, height: 18, fontSize: '0.65rem' }}
      />
    ) : (
      score !== undefined && (
        <Chip
          // No i18n key: a rounded percentage carries no words to translate.
          label={`${Math.round(score * 100)}%`}
          size="small"
          variant="outlined"
          sx={{ flexShrink: 0, height: 18, fontSize: '0.65rem', fontVariantNumeric: 'tabular-nums' }}
        />
      )
    )}
  </Box>
));

interface DocumentClassificationBadgeProps {
  classificationMetadata: DocumentClassificationMetadataDto | null;
  isClassifying?: boolean;
  /** Generic failure message for a failure the client saw itself (request
   * error, poll timeout). A key, never raw error text. */
  classificationErrorKey?: SingularKeys;
  onRetry?: () => void;
  isDuplicate?: boolean;
  /** Move the document to its AI-suggested domain (wrong-domain docs only). */
  onMove?: () => void | Promise<void>;
  /** True while a move request for this document is in flight. */
  isMoving?: boolean;
  /**
   * Re-match this document to one of the AI's ranked candidate documents.
   * Omit to render the candidates as read-only information.
   */
  /**
   * `targetDomainKey` is the domain the chosen candidate belongs to, which is
   * not always the domain being viewed: a rerouted document offers alternatives
   * from two. Empty means "the domain this document is already in".
   */
  onApplyCandidate?: (
    standardDocumentKey: string,
    targetDomainKey?: string,
  ) => void | Promise<void>;
  /** True while a candidate switch for this document is in flight. */
  isApplyingCandidate?: boolean;
  /**
   * Standard document keys already matched by *other* documents in this domain.
   * A candidate in this set is still offered, but badged as taken — picking it
   * would collide with the other document and be rejected by the API.
   */
  claimedDocumentKeys?: ReadonlySet<string>;
}

export const DocumentClassificationBadge = observer(({
  classificationMetadata,
  isClassifying = false,
  classificationErrorKey,
  onRetry,
  isDuplicate,
  onMove,
  isMoving = false,
  onApplyCandidate,
  isApplyingCandidate = false,
  claimedDocumentKeys,
}: DocumentClassificationBadgeProps) => {
  const { t } = useTrans();
  const { dir } = useDirection();
  const [expandedOverride, setExpandedOverride] = useState<boolean | null>(null);
  const [suggestionExpanded, setSuggestionExpanded] = useState(false);
  const [moveConfirmOpen, setMoveConfirmOpen] = useState(false);
  const [pendingCandidate, setPendingCandidate] = useState<DocumentClassificationCandidate | null>(
    null,
  );
  // null = "not touched yet", which resolves to the current match below. Kept
  // separate from the matched key so the radio reflects the user's intent
  // before they confirm it.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  /**
   * Candidates split into per-domain groups, in first-appearance order.
   *
   * A rerouted document is offered alternatives from two domains at once — the
   * one it was filed under and the one the AI thinks it belongs to — and a flat
   * list of document names gives no way to tell which is which.
   *
   * The array order is preserved throughout: it arrives ranked by score across
   * both domains, so the group holding the single best candidate comes first and
   * each group stays internally ranked. Never re-sorted, and grouping is
   * presentation only.
   *
   * Must stay here, above the early returns, and read the metadata defensively
   * rather than the narrowed `candidates` below it. Every render on the way to
   * the picker can bail out — to a spinner, an alert, or null — so a hook placed
   * at its point of use runs on some renders and not others. That crashed the
   * page: a document flipping from "analyzing" to a finished match re-renders
   * this component in place with one hook more than the render before it, which
   * is React error #310, "Rendered more hooks than during the previous render".
   * Clicking retry on a matched document did the same in reverse.
   */
  const candidateGroups = useMemo(() => {
    const groups: {
      domainKey: string;
      domainName: string;
      rows: DocumentClassificationCandidate[];
    }[] = [];
    for (const candidate of classificationMetadata?.candidateDocuments ?? []) {
      const domainKey = candidate.domainKey ?? '';
      const group = groups.find((entry) => entry.domainKey === domainKey);
      if (group) {
        group.rows.push(candidate);
      } else {
        groups.push({ domainKey, domainName: candidate.domainName ?? '', rows: [candidate] });
      }
    }
    return groups;
  }, [classificationMetadata?.candidateDocuments]);

  const handleConfirmCandidate = () => {
    const candidate = pendingCandidate;
    setPendingCandidate(null);
    if (candidate) void onApplyCandidate?.(candidate.documentKey, candidate.domainKey);
  };

  const handleConfirmMove = () => {
    // Close the dialog right away and let the move run in the background — the
    // document card shows its own "moving…" overlay (driven by `isMoving`)
    // until it succeeds (card is removed) or fails (error snackbar). moveDocument
    // handles success/error internally, so this is fire-and-forget.
    setMoveConfirmOpen(false);
    void onMove?.();
  };

  // ── Classifying spinner ──────────────────────────────────────────────────
  // Differentiates the two server-side in-flight phases so the user knows
  // whether the doc is queued (PENDING — waiting for the worker to pick it
  // up) vs being actively processed by the AI (PROCESSING). Also keeps the
  // spinner visible on page refresh: as long as the server reports
  // PENDING/PROCESSING, we render this branch even if the hook's local
  // `isClassifying` state hasn't been set yet.
  const serverStatus = classificationMetadata?.classificationStatus;
  const isPending = serverStatus === DocumentClassificationStatus.PENDING;
  const isProcessing = serverStatus === DocumentClassificationStatus.PROCESSING;

  if (isClassifying || isPending || isProcessing) {
    const messageKey = isProcessing
      ? 'app.assessment.classification.processing'
      : isPending
        ? 'app.assessment.classification.queued'
        : 'app.assessment.classification.classifying';
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <CircularProgress size={16} thickness={5} />
        <Typography variant="caption" color="text.secondary">
          {t(messageKey)}
        </Typography>
      </Box>
    );
  }

  // ── Classification failed ───────────────────────────────────────────────
  // Client-side failures and server-reported FAILED render the same generic
  // copy; the real cause never leaves the API logs.
  const failureMessageKey: SingularKeys | null =
    classificationErrorKey ??
    (classificationMetadata?.classificationStatus === DocumentClassificationStatus.FAILED
      ? 'app.assessment.classification.processingFailed.description'
      : null);

  if (failureMessageKey) {
    return (
      <Alert
        severity="error"
        sx={{ mt: 1 }}
        action={
          onRetry && (
            <TooltipCard variant="dark" description={t('app.assessment.classification.retry')}>
              <IconButton size="small" onClick={onRetry} color="inherit">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </TooltipCard>
          )
        }
      >
        <AlertTitle sx={{ fontWeight: 600, mb: 0.25, fontSize: 'inherit' }}>
          {t('app.assessment.classification.processingFailed.title')}
        </AlertTitle>
        <Typography variant="caption">{t(failureMessageKey)}</Typography>
      </Alert>
    );
  }

  // ── No metadata yet (pre-classification) ────────────────────────────────
  if (!classificationMetadata || classificationMetadata.classificationStatus !== DocumentClassificationStatus.COMPLETED) {
    return null;
  }

  const invalidReason = classificationMetadata.invalidReason;
  const na = t('app.assessment.classification.na');

  // Accepted but non-standard format is NOT an invalid document: it matched
  // and is submitted, so it falls through to the matched view below and is
  // rendered there as that view plus a warning, rather than as a rejection.
  const isNonStandardFormat =
    invalidReason === DocumentClassificationInvalidReason.WRONG_FORMAT;

  // A weak match is likewise not a rejection: the AI matched the *nearest*
  // standard document, the document is included and graded, and the user is
  // warned plus offered the runner-ups. It renders as the matched view.
  const isWeakMatch = isWeakClassificationMatch(invalidReason);

  const candidates = classificationMetadata.candidateDocuments ?? [];
  const coverage = classificationMetadata.coverage;

  const confidence =
    (classificationMetadata.classificationConfidence as ClassificationConfidence | null) ??
    ClassificationConfidence.LOW;
  const tone = CONFIDENCE_TONE[confidence] ?? CONFIDENCE_TONE[ClassificationConfidence.LOW];
  const hasMatchedKey = Boolean(classificationMetadata.matchedStandardDocumentKey?.trim());


  // Untouched selection means the current match, so the picker opens showing
  // what we chose rather than nothing.
  const effectiveSelectedKey =
    selectedKey ?? classificationMetadata.matchedStandardDocumentKey ?? null;
  // Confirming the current match is a real answer, not a no-op: it is how the
  // user says "you were right", which clears the weak flag and releases the
  // domain. So any resolvable selection can be confirmed.
  const canConfirmSelection =
    Boolean(effectiveSelectedKey) &&
    (effectiveSelectedKey === classificationMetadata.matchedStandardDocumentKey ||
      candidates.some((c) => c.documentKey === effectiveSelectedKey));
  const isThinDocument =
    coverage === DocumentClassificationCoverage.PARTIAL ||
    coverage === DocumentClassificationCoverage.MINIMAL;

  // Only worth interrupting the user when the match is genuinely uncertain. A
  // high-confidence match can still surface candidates, but showing a "did we
  // get this wrong?" picker on every clean document would train people to
  // ignore it.
  // Shown for any weak match, even with no candidates: the current match is
  // itself a row, so Confirm is always reachable. Gating on candidates left a
  // weak match with none — reachable whenever the catalog rejects every
  // suggested key — with no way to answer and no way to leave the domain.
  // A medium match opens it too — but only while there is something to choose
  // between. "Medium" is the classifier saying it is not sure, and the file list
  // gives no other place to say so; the user reads a matched document and moves
  // on, never learning the second-best scored almost the same.
  //
  // The `candidates.length` half is what makes it dismissable. Confirming a
  // match settles the confidence at MEDIUM and clears the alternatives, so
  // without it every confirmed document would re-open this panel showing its own
  // match as the only option — asking the user to confirm what they just
  // confirmed, with no way to make it stop.
  //
  // A weak match still opens with no candidates, deliberately: it blocks the
  // domain, so Confirm has to stay reachable. Medium blocks nothing.
  const isUncertainMatch =
    confidence === ClassificationConfidence.MEDIUM && candidates.length > 0;
  const showCandidatePicker =
    isWeakMatch || isUncertainMatch || (isThinDocument && candidates.length > 0);
  // A document the user still has to decide about. Drives the collapsed-row cue
  // and the default open state; the same predicate gates domain navigation.
  // Matches the save gate exactly. Including coverage here promised the user a
  // decision they would be blocked on, when only a weak match actually blocks.
  const needsDecision = isWeakMatch;
  const expanded = expandedOverride ?? needsDecision;

  /**
   * Ranked candidate documents, best fit first.
   *
   * The order is meaningful and is rendered exactly as received — never
   * re-sorted. Candidates already matched by another document in this domain
   * stay visible but are badged and disabled: hiding them would leave the user
   * wondering why the option they expected is missing.
   */
  const renderCandidates = () => {
    const currentName =
      classificationMetadata.matchedStandardDocumentName ||
      classificationMetadata.matchedStandardDocumentKey ||
      na;

    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" gap={1} flexWrap="wrap">
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
            color="text.secondary"
          >
            {t('app.assessment.classification.candidates.title')}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {t('app.assessment.classification.candidates.hint')}
          </Typography>
        </Stack>

        {/* The current match is the first option, so rejecting every
            alternative is a choice the user can actually make rather than a
            dead end. Order below it is the AI's ranking, rendered as received. */}
        {hasMatchedKey && (
          <CandidateRow
            name={currentName}
            why={t('app.assessment.classification.candidates.currentWhy')}
            selected={effectiveSelectedKey === classificationMetadata.matchedStandardDocumentKey}
            current
            onSelect={() =>
              setSelectedKey(classificationMetadata.matchedStandardDocumentKey ?? null)
            }
          />
        )}

        {candidateGroups.map(({ domainKey, domainName, rows }) => (
          <Fragment key={domainKey || 'current'}>
            {/* Only when a reroute put two domains in play. With one domain the
                header would state where the user already knows they are. The
                name comes from the candidate itself, so no lookup and no key to
                translate. */}
            {candidateGroups.length > 1 && (
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{ mt: 0.5 }}
              >
                {domainName || domainKey}
              </Typography>
            )}
            {rows.map((candidate) => (
              <CandidateRow
                key={candidate.documentKey}
                name={candidate.documentName || candidate.documentKey}
                score={candidate.score}
                selected={effectiveSelectedKey === candidate.documentKey}
                claimed={Boolean(claimedDocumentKeys?.has(candidate.documentKey))}
                claimedLabel={t('app.assessment.classification.candidates.alreadyMatched')}
                onSelect={() => setSelectedKey(candidate.documentKey)}
              />
            ))}
          </Fragment>
        ))}

        {onApplyCandidate && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.25 }}>
            <Button
              size="small"
              variant="contained"
              disabled={!canConfirmSelection || isApplyingCandidate}
              onClick={() => {
                const next = candidates.find((c) => c.documentKey === effectiveSelectedKey);
                setPendingCandidate(
                  // Falls back to the current match, which is a row too —
                  // confirming it is how the user says "you were right". It is
                  // not in `candidates`, so it is synthesised here, and its
                  // domain is deliberately blank: the current match is by
                  // definition already in this domain, and naming one would ask
                  // the API for a move that must not happen. It has no score
                  // either — its similarity was never queried, and 0 would read
                  // as a measured zero.
                  next ?? {
                    documentKey: effectiveSelectedKey as string,
                    documentName: classificationMetadata.matchedStandardDocumentName ?? '',
                    domainKey: '',
                    domainName: '',
                    score: 0,
                  },
                );
              }}
              startIcon={isApplyingCandidate ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              {t('app.assessment.classification.candidates.confirm')}
            </Button>
          </Box>
        )}
      </Box>
    );
  };


  const candidateConfirmButtons: ConfirmationButton[] = [
    {
      label: t('app.assessment.classification.warning.cancel'),
      onClick: () => setPendingCandidate(null),
      variant: 'outlined',
      disabled: isApplyingCandidate,
    },
    {
      label: t('app.assessment.classification.candidates.useThis'),
      onClick: handleConfirmCandidate,
      variant: 'contained',
      color: 'warning',
      isLoading: isApplyingCandidate,
    },
  ];

  const candidateConfirmModal = onApplyCandidate ? (
    <ConfirmationModal
      open={Boolean(pendingCandidate)}
      onClose={() => setPendingCandidate(null)}
      isLoading={isApplyingCandidate}
      icon={<LightbulbIcon size={22} weight="fill" />}
      iconColor="warning"
      title={t('app.assessment.classification.candidates.confirmTitle')}
      content={
        <Typography variant="body1" color="text.secondary">
          {t('app.assessment.classification.candidates.confirmMessage', {
            from:
              classificationMetadata.matchedStandardDocumentName ||
              classificationMetadata.matchedStandardDocumentKey ||
              na,
            to: pendingCandidate?.documentName || pendingCandidate?.documentKey || na,
          })}
        </Typography>
      }
      buttons={candidateConfirmButtons}
    />
  ) : null;

  // ── Invalid document (no match / wrong domain / unsupported format) ──────
  if (invalidReason && !isNonStandardFormat && !isWeakMatch) {
    const isWrongDomain = invalidReason === DocumentClassificationInvalidReason.WRONG_DOMAIN;

    const formatNameAndKey = (name: string | null | undefined, key: string | null | undefined): string | null => {
      const trimmedName = name?.trim();
      const trimmedKey = key?.trim();

      if (trimmedName && trimmedKey) return `${trimmedName} (${trimmedKey})`;
      return trimmedName || trimmedKey || null;
    };

    const suggestedDomainLabel = formatNameAndKey(
      classificationMetadata.suggestedDomainName,
      classificationMetadata.suggestedDomainKey,
    ) || na;

    const matchedDocumentLabel = formatNameAndKey(
      classificationMetadata.matchedStandardDocumentName,
      classificationMetadata.matchedStandardDocumentKey,
    );

    const matchedDocumentSection = matchedDocumentLabel
      ? ` ${t('app.assessment.classification.standardDocument')}: ${matchedDocumentLabel}.`
      : '';

    const isUnsupportedFormat =
      invalidReason === DocumentClassificationInvalidReason.UNSUPPORTED_FORMAT;

    const title = isWrongDomain
      ? t('app.assessment.classification.invalid.wrongDomain.title')
      : isUnsupportedFormat
        ? t('app.assessment.classification.unsupportedFormat.title')
        : t('app.assessment.classification.invalid.title');

    const description = isWrongDomain
      ? t('app.assessment.classification.invalid.wrongDomain.description', {
        suggestedDomain: suggestedDomainLabel,
        matchedDocumentSection,
      })
      : isUnsupportedFormat
        ? t('app.assessment.classification.unsupportedFormat.description')
        : t('app.assessment.classification.invalid.description');

    // Showing the suggestion needs only a hint; offering the move needs both
    // keys, because the move relinks the document to a standard-document row in
    // the target domain. A domain-only suggestion therefore renders as
    // information without a button that would fail on click.
    const hasSuggestionKeys =
      Boolean(classificationMetadata.suggestedDocumentKey) ||
      Boolean(classificationMetadata.suggestedDomainKey);

    const hasSuggestion = isWrongDomain && hasSuggestionKeys;
    const canMove = canMoveToSuggestedDomain(classificationMetadata);

    const moveConfirmButtons: ConfirmationButton[] = [
      {
        label: t('app.assessment.classification.warning.cancel'),
        onClick: () => setMoveConfirmOpen(false),
        variant: 'outlined',
        disabled: isMoving,
      },
      {
        label: t('app.assessment.classification.suggestion.moveConfirmCta'),
        onClick: handleConfirmMove,
        variant: 'contained',
        color: 'info',
        isLoading: isMoving,
        startIcon: <ArrowBendUpRightIcon size={16} weight="bold" />,
      },
    ];

    return (
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <NoFlipAlert
          mode={dir}
          severity="warning"
          icon={<WarningCircleIcon weight="fill" />}
          sx={{ alignItems: 'flex-start' }}
          action={
            onRetry && (
              <TooltipCard variant="dark" description={t('app.assessment.classification.retry')}>
                <IconButton size="small" color="inherit" onClick={onRetry}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </TooltipCard>
            )
          }
        >
          <AlertTitle sx={{ fontWeight: 600, mb: 0.25 }}>
            {title}
          </AlertTitle>
          <Typography variant="caption" component="span">
            {description}
          </Typography>
        </NoFlipAlert>

        {hasSuggestion && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'info.light',
              borderRadius: 2,
              bgcolor: 'info.lighter',
              overflow: 'hidden',
            }}
          >
            {/* Header — clickable to toggle */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              onClick={() => setSuggestionExpanded((prev) => !prev)}
              sx={{ px: 1.5, py: 1, bgcolor: 'info.light', opacity: 0.9, cursor: 'pointer', userSelect: 'none' }}
            >
              <LightbulbIcon size={16} weight="fill" />
              <Typography variant="caption" fontWeight={600} sx={{ flex: 1 }}>
                {t('app.assessment.classification.suggestion.title')}
              </Typography>
              <IconButton size="small" tabIndex={-1}>
                {suggestionExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Stack>

            <Collapse in={suggestionExpanded}>
              <Box sx={{ px: 1.5, py: 1.25, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('app.assessment.classification.suggestion.description')}
                </Typography>

                <Divider />

                <Stack direction="row" spacing={2} flexWrap="wrap">
                  {/* Suggested Document */}
                  {classificationMetadata.suggestedDocumentKey && (
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {t('app.assessment.classification.suggestion.suggestedDocument')}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {classificationMetadata.suggestedDocumentName || na}
                      </Typography>
                      <Chip
                        label={classificationMetadata.suggestedDocumentKey}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.5, fontSize: '0.7rem', height: 20, borderColor: 'info.main', color: 'info.dark' }}
                      />
                    </Box>
                  )}

                  {/* Suggested Domain */}
                  {classificationMetadata.suggestedDomainKey && (
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {t('app.assessment.classification.suggestion.suggestedDomain')}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {classificationMetadata.suggestedDomainName || na}
                      </Typography>
                      <Chip
                        label={classificationMetadata.suggestedDomainKey}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.5, fontSize: '0.7rem', height: 20, borderColor: 'info.main', color: 'info.dark' }}
                      />
                    </Box>
                  )}


                </Stack>

                {/* Suggested Reasoning */}
                {classificationMetadata.suggestedAiReasoning && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {t('app.assessment.classification.suggestion.suggestedReasoning')}
                    </Typography>
                    <Box
                      sx={{
                        p: 1,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        '& p': { m: 0, fontSize: '0.8rem', lineHeight: 1.5 },
                        '& p + p': { mt: 0.75 },
                      }}
                    >
                      <ReactMarkdown>{classificationMetadata.suggestedAiReasoning}</ReactMarkdown>
                    </Box>
                  </Box>
                )}

                {/* One-click move to the AI-suggested domain (no re-upload). */}
                {onMove && canMove && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="info"
                      size="small"
                      disabled={isMoving}
                      onClick={() => setMoveConfirmOpen(true)}
                      startIcon={
                        isMoving ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <ArrowBendUpRightIcon size={16} weight="bold" />
                        )
                      }
                    >
                      {isMoving
                        ? t('app.assessment.classification.suggestion.moving')
                        : t('app.assessment.classification.suggestion.moveButton', {
                            domain: classificationMetadata.suggestedDomainName || na,
                          })}
                    </Button>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Confirmation before moving the document to the suggested domain. */}
        {onMove && canMove && (
          <ConfirmationModal
            open={moveConfirmOpen}
            onClose={() => setMoveConfirmOpen(false)}
            isLoading={isMoving}
            icon={<ArrowBendUpRightIcon size={22} weight="bold" />}
            iconColor="info"
            title={t('app.assessment.classification.suggestion.moveConfirmTitle')}
            content={
              <Typography variant="body1" color="text.secondary">
                {t('app.assessment.classification.suggestion.moveConfirmMessage', {
                  domain: classificationMetadata.suggestedDomainName || na,
                })}
              </Typography>
            }
            buttons={moveConfirmButtons}
          />
        )}
      </Box>
    );
  }

  // ── Matched document (clean, weak, or non-standard format) ───────────────
  // A weak match renders here rather than in the invalid branch above: the
  // document IS included in the assessment and graded, so showing it as a
  // rejection would misrepresent what happens to it.
  return (
    <Box sx={{ mt: 1 }}>
      {/* Match row — a status dot, what we matched, and how sure we are.
          Deliberately carries no numeric score: the AI reports high/medium/low,
          not a percentage, and rendering a number derived from a label would
          invent precision that does not exist. */}
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{
          px: 1.25,
          py: 1,
          borderRadius: 1,
          border: '1px solid',
          borderColor: tone.border,
          bgcolor: tone.surface,
          cursor: 'pointer',
          '&:hover': { filter: 'brightness(0.98)' },
        }}
        onClick={() => setExpandedOverride(!expanded)}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: tone.dot,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {t(
              hasMatchedKey
                ? 'app.assessment.classification.matchRow.matched'
                : 'app.assessment.classification.matchRow.noMatch',
            )}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            noWrap
          >
            {classificationMetadata.matchedStandardDocumentName || na}
            {isDuplicate && (
              <Badge
                variant="error"
                label={<Trans i18nKey="app.assessment.workflow.duplicate" />}
                sx={{ flexShrink: 0 }}
              />
            )}
          </Typography>
        </Box>
        {/* Survives collapsing, so folding the panel away never hides that
            something is owed. Icon plus the headline only — the explanation
            lives inside the panel rather than being repeated on the row. */}
        {needsDecision ? (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
            <WarningCircleIcon size={16} weight="fill" color="#F59E0B" />
            <Typography variant="caption" fontWeight={700} color="warning.dark">
              {t(
                isWeakMatch
                  ? 'app.assessment.classification.weak.badge'
                  : 'app.assessment.classification.incomplete.badge',
              )}
            </Typography>
          </Stack>
        ) : (
          <Chip
            label={t(CONFIDENCE_LABEL[confidence] ?? CONFIDENCE_LABEL[ClassificationConfidence.LOW])}
            size="small"
            variant="outlined"
            sx={{
              flexShrink: 0,
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 700,
              color: tone.text,
              borderColor: tone.border,
              bgcolor: 'background.paper',
            }}
          />
        )}
        <IconButton size="small" sx={{ flexShrink: 0 }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Stack>

      {candidateConfirmModal}

      <Collapse in={expanded}>
        <Stack spacing={1.5} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, mt: 1 }}>
          {/* Everything that needs a decision now lives inside this one
              collapsible, so a domain full of files fits on screen. It opens by
              default when a decision is pending and the user can fold it away;
              they cannot leave the domain without resolving it, so collapsing
              can no longer hide it. */}
          {(isWeakMatch || isThinDocument) && (
          <NoFlipAlert
            mode={dir}
            severity="warning"
            icon={<WarningCircleIcon weight="fill" />}
            sx={{ alignItems: 'flex-start' }}
          >
            <AlertTitle sx={{ fontWeight: 600, mb: 0.25 }}>
              {isWeakMatch
                ? t('app.assessment.classification.weak.title')
                : t('app.assessment.classification.incomplete.title')}
            </AlertTitle>
            <Typography variant="caption" component="span" display="block">
              {isWeakMatch
                ? t('app.assessment.classification.weak.description')
                : t('app.assessment.classification.incomplete.description')}
            </Typography>
            {/* The section list is deliberately not rendered. It runs to twenty-odd
                catalog section names — longer than the card it sits in, and it is
                our catalog's structure rather than anything the reader can act on.
                "A lot is missing, expect substantial work" is the whole message. */}
          </NoFlipAlert>
          )}

          {showCandidatePicker && renderCandidates()}

          {/* Warning first — why the row is flagged, before the match detail
              that explains what it was flagged against. */}
          {isNonStandardFormat && (
            <NoFlipAlert
              mode={dir}
              severity="warning"
              // Same glyph, size and amber as the badge on the file row above —
              // MUI's severity colour would otherwise render a different amber.
              icon={<WarningCircleIcon size={18} weight="fill" color="#F59E0B" />}
              sx={{ alignItems: 'flex-start' }}
            >
              <AlertTitle sx={{ fontWeight: 600, mb: 0.25 }}>
                {t('app.assessment.classification.nonStandardFormat.title')}
              </AlertTitle>
              <Typography variant="caption" component="span">
                {t('app.assessment.classification.nonStandardFormat.description', {
                  document:
                    classificationMetadata.matchedStandardDocumentName?.trim() ||
                    classificationMetadata.matchedStandardDocumentKey?.trim() ||
                    na,
                })}
              </Typography>
            </NoFlipAlert>
          )}

          {/* Standard Document */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              {t('app.assessment.classification.standardDocument')}
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {classificationMetadata.matchedStandardDocumentName || na}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('app.assessment.classification.standardDocumentKey', {
                key: classificationMetadata.matchedStandardDocumentKey || na,
              })}
            </Typography>
          </Box>

          {/* Matched Domain */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              {t('app.assessment.classification.matchedDomain')}
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {classificationMetadata.matchedDomainName || na}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('app.assessment.classification.standardDocumentKey', {
                key: classificationMetadata.matchedDomainKey || na,
              })}
            </Typography>
          </Box>

          {/* Metadata row */}
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('app.assessment.classification.language')}
              </Typography>
              <Typography variant="body2">
                {classificationMetadata.language?.toUpperCase() || na}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('app.assessment.classification.wordCount')}
              </Typography>
              <Typography variant="body2">
                {classificationMetadata.wordCount?.toLocaleString() || na}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('app.assessment.classification.pageCount')}
              </Typography>
              <Typography variant="body2">
                {classificationMetadata.pageCount?.toLocaleString() || na}
              </Typography>
            </Box>
          </Stack>

          {/* AI Reasoning */}
          {classificationMetadata.reasoning && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                {t('app.assessment.classification.aiReasoning')}
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  fontStyle: 'italic',
                  '& p': { m: 0, fontSize: '0.875rem', lineHeight: 1.6 },
                  '& p + p': { mt: 1 },
                  '& ul, & ol': { mt: 0.5, mb: 0, pl: 2.5, fontSize: '0.875rem' },
                  '& li': { mb: 0.25 },
                  '& strong': { fontWeight: 600 },
                  '& code': {
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    bgcolor: 'action.selected',
                    px: 0.5,
                    borderRadius: 0.5,
                  },
                }}
              >
                <ReactMarkdown>{classificationMetadata.reasoning}</ReactMarkdown>
              </Box>
            </Box>
          )}

          <Typography variant="caption" color="text.secondary">
            {t('app.assessment.classification.classifiedAt', {
              date: classificationMetadata.classifiedAt
                ? new Date(classificationMetadata.classifiedAt).toLocaleString()
                : na,
            })}
          </Typography>
        </Stack>
      </Collapse>
    </Box>
  );
});
